import { Info, RefreshCw, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useSchedulesVerificationGlobal } from '@/hooks/useSchedulesVerificationGlobal';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface SchedulesVerificationGlobalPanelProps {
  className?: string;
  enabled?: boolean;
}

export function SchedulesVerificationGlobalPanel({
  className,
  enabled = true,
}: SchedulesVerificationGlobalPanelProps) {
  const { toast } = useToast();
  const {
    summary,
    unavailable,
    isLoading,
    isVerifyingAll,
    error,
    verifyAll,
    refresh,
  } = useSchedulesVerificationGlobal(enabled);

  const issueControllers =
    (summary?.pendingControllers ?? 0) + (summary?.errorControllers ?? 0);
  const hasIssues = issueControllers > 0;

  const handleVerifyAll = async () => {
    try {
      await verifyAll();
      toast({
        title: 'Verificación masiva iniciada',
        description:
          'Se está verificando todas las controladoras en segundo plano. Actualice en unos segundos.',
      });
      setTimeout(() => {
        void refresh();
      }, 3000);
    } catch (e) {
      toast({
        title: 'No se pudo verificar',
        description: e instanceof Error ? e.message : 'Error desconocido',
        variant: 'destructive',
      });
    }
  };

  return (
    <Card
      className={cn(
        unavailable
          ? 'border-border'
          : hasIssues
            ? 'border-amber-500/40'
            : summary
              ? 'border-green-500/30'
              : undefined,
        className
      )}
    >
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2 space-y-0">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" />
          Verificación de programaciones (servidor)
        </CardTitle>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void refresh()}
            disabled={isLoading || isVerifyingAll}
          >
            <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void handleVerifyAll()}
            disabled={isVerifyingAll || isLoading}
          >
            <RefreshCw
              className={cn('h-4 w-4 mr-2', isVerifyingAll && 'animate-spin')}
            />
            Verificar todas
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {unavailable && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Resumen global no disponible en este servidor. La verificación por
              controladora sigue disponible en el detalle cuando el backend v0.7 esté activo.
            </AlertDescription>
          </Alert>
        )}

        {error && !unavailable && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!unavailable && summary && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <div>
                <p className="text-2xl font-semibold">{summary.totalControllers}</p>
                <p className="text-xs text-muted-foreground">Controladoras</p>
              </div>
              <div>
                <p className="text-2xl font-semibold text-green-700">
                  {summary.verifiedControllers}
                </p>
                <p className="text-xs text-muted-foreground">Verificadas</p>
              </div>
              <div>
                <p className="text-2xl font-semibold text-amber-700">
                  {summary.pendingControllers}
                </p>
                <p className="text-xs text-muted-foreground">Pendientes</p>
              </div>
              <div>
                <p className="text-2xl font-semibold text-red-600">
                  {summary.errorControllers}
                </p>
                <p className="text-xs text-muted-foreground">Con error</p>
              </div>
            </div>
            {(summary.lastVerificationRun || summary.nextScheduledRun) && (
              <p className="text-xs text-muted-foreground text-center">
                {summary.lastVerificationRun && (
                  <>
                    Última ejecución:{' '}
                    {new Date(summary.lastVerificationRun).toLocaleString('es-ES', {
                      timeZone: 'Europe/Madrid',
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </>
                )}
                {summary.nextScheduledRun && (
                  <>
                    {summary.lastVerificationRun ? ' · ' : ''}
                    Próxima:{' '}
                    {new Date(summary.nextScheduledRun).toLocaleString('es-ES', {
                      timeZone: 'Europe/Madrid',
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </>
                )}
              </p>
            )}
          </>
        )}

        {!unavailable && !summary && !isLoading && !error && (
          <p className="text-sm text-muted-foreground text-center py-2">
            Sin datos de verificación global todavía.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
